import queryClient from '../../../ReactQuery/client';
import useMutation from '../../../ReactQuery/useMutation';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { post } from '../../../redux/api';
import type { TeamWithAssociations } from '../../../../shared/types/routes/teams';

import { teamGroupsQueryKey } from './teamGroups/useTeamGroups';
import { getTeamsQueryKey } from './useTeams';

type NewTeam = Pick<TeamWithAssociations, 'name' | 'defaultRoleId' | 'autoAddUsers' | 'uniqueIdentifier'> & {
  teamGroupIds?: number[] | null;
};

// Function to create a team, returning just the data for React Query
async function createTeam(team: NewTeam): Promise<TeamWithAssociations> {
  const response = await post<NewTeam, TeamWithAssociations>('/api/teams', team);
  // Return just the data for React Query to match its type expectations
  return response.data as TeamWithAssociations;
}

/**
 * Original useCreateTeam hook without toast notifications.
 * This is useful when you want to handle success/error states manually.
 */
export function useCreateTeamWithoutToast() {
  return useMutation({
    mutationFn: createTeam,
    onSuccess: (createdTeam: TeamWithAssociations) => {
      // First we add the new team to the cache, so that it appears immediately.
      queryClient.setQueryData(getTeamsQueryKey, (oldTeams: TeamWithAssociations[] | undefined) => {
        return [...(oldTeams ?? []), createdTeam];
      });
      // Then we invalidate the teams query, so that the new full team data is fetched.
      queryClient.invalidateQueries(getTeamsQueryKey);
      // Team could have been added to a team group, so we invalidate the team groups query as well.
      queryClient.invalidateQueries(teamGroupsQueryKey);
    },
  });
}

/**
 * useCreateTeam is a hook that creates a team, shows toast notifications for success/errors,
 * and invalidates the teams query.
 */
export default function useCreateTeam() {
  return useToastedMutation({
    mutationFn: createTeam,
    successToastMessage: (data: TeamWithAssociations) => `Team "${data.name}" created successfully`,
    errorToastMessage: 'An error occurred while creating the team.',
    onSuccess: (createdTeam: TeamWithAssociations) => {
      // First we add the new team to the cache, so that it appears immediately.
      queryClient.setQueryData(getTeamsQueryKey, (oldTeams: TeamWithAssociations[] | undefined) => {
        return [...(oldTeams ?? []), createdTeam];
      });
      // Then we invalidate the teams query, so that the new full team data is fetched.
      queryClient.invalidateQueries(getTeamsQueryKey);
      // Team could have been added to a team group, so we invalidate the team groups query as well.
      queryClient.invalidateQueries(teamGroupsQueryKey);
    },
  });
}
